<div class="opportunities-dashboard">
  <section class="analytics-card workspace-card">

      <!-- GA Connection Warning Banner -->
      <p-messages
        *ngIf="!isGaConnected() && !isLoading && allItems.length > 0"
        severity="warn"
        [closable]="true"
        styleClass="mb-4"
        [value]="[{ 
          severity: 'warn', 
          summary: 'GA4 Not Connected', 
          detail: getGaErrorMessage() || 'Google Analytics 4 is not connected. Some advanced insights (Campaign Halo, Traffic Quality Leak) will be unavailable. Connect GA4 in Project Settings to enable these features.' 
        }]">
      </p-messages>

      <!-- Page Header Row -->
      <div class="workspace-toolbar">
        <div>
          <h2>Opportunities</h2>
          <p>Prioritize high-impact opportunities and track execution status.</p>
        </div>
        <div class="toolbar-actions">
          <button
            pButton
            label="Inspect URL"
            icon="pi pi-search"
            class="p-button-sm p-button-outlined toolbar-action"
            [disabled]="isInspecting"
            (click)="onOpenInspectDialog()">
          </button>

          <button
            pButton
            [label]="isRefreshing ? 'Running...' : 'Run Analysis'"
            icon="pi pi-refresh"
            [disabled]="isRefreshing"
            [loading]="isRefreshing"
            class="p-button-sm p-button-outlined toolbar-action"
            (click)="onRunAnalysis()">
          </button>
        </div>
      </div>

      <!-- KPI Summary Bar -->
      <gig-opportunities-kpi-bar [items]="allItems"></gig-opportunities-kpi-bar>

      <!-- Source Health -->
      <section *ngIf="sourceChecks.length > 0" class="source-health-section">
        <div class="source-health-header">
          <h3>Source Health</h3>
          <p>See which integrations and datasets are powering the current opportunity set.</p>
        </div>

        <div class="source-health-grid">
          <article *ngFor="let sourceCheck of sourceChecks" class="source-health-card">
            <div class="source-health-card-head">
              <span class="source-health-label">
                <i [class]="getSourceIconClass(sourceCheck.sourceKey)"></i>
                {{ sourceCheck.label }}
              </span>
              <span class="source-status-badge" [ngClass]="getSourceStatusClass(sourceCheck.status)">
                {{ getSourceStatusLabel(sourceCheck.status) }}
              </span>
            </div>

            <p class="source-health-detail">{{ sourceCheck.detail }}</p>

            <div class="source-health-meta" *ngIf="sourceCheck.lastSyncedAt || sourceCheck.actionLabel">
              <span *ngIf="sourceCheck.lastSyncedAt">
                Last synced {{ sourceCheck.lastSyncedAt | date:'short' }}
              </span>
              <span *ngIf="sourceCheck.actionLabel" class="source-health-action">
                {{ sourceCheck.actionLabel }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <!-- Sub-tab Filter Bar -->
      <div class="platform-selector opportunities-selector">
        <div class="platform-tabs-container">
          <div class="platform-tabs">
            <button
              *ngFor="let tab of tabs; let i = index"
              type="button"
              [class.active]="activeTabIndex === i"
              (click)="onTabChange(i)">
              <span class="tab-item-content">
                <i [class]="getTabIconClass(i)"></i>
                <span class="tab-label">{{ tab.label }} ({{ tabCount(tab) }})</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-state">
        <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
      </div>

      <!-- Error State -->
      <p-messages
        *ngIf="!isLoading && loadError"
        severity="error"
        [value]="[{ severity: 'error', summary: 'Error', detail: 'Failed to load opportunities. Please try again.' }]">
      </p-messages>

      <!-- Empty State -->
      <div
        *ngIf="!isLoading && !loadError && filteredItems.length === 0"
        class="empty-state">
        <i class="pi pi-inbox text-5xl mb-3 text-300"></i>
        <span>{{ getEmptyStateMessage() }}</span>
      </div>

      <!-- Opportunities Table -->
      <ng-container *ngIf="!isLoading && !loadError && filteredItems.length > 0">
        <div class="table-shell">
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="filteredItems"
            [sortField]="'priority'"
            [sortOrder]="1"
            [paginator]="true"
            [rows]="25"
            [rowsPerPageOptions]="[10, 25, 50]"
            [rowHover]="true"
            responsiveLayout="scroll"
            styleClass="p-datatable-sm p-datatable-gridlines-none vertical-align-middle opportunities-table"
            selectionMode="single"
            (onRowSelect)="onRowSelect($event.data)">

            <ng-template pTemplate="header">
              <tr class="text-500 font-bold bg-white">
                <th pResizableColumn pSortableColumn="type" style="width: 140px" class="pl-4 py-3 font-bold text-left">
                  Type <p-sortIcon field="type"></p-sortIcon>
                </th>
                <th pResizableColumn class="py-3 font-bold text-left">Title</th>
                <th pResizableColumn pSortableColumn="effort" style="width: 110px" class="py-3 font-bold text-left">
                  Effort <p-sortIcon field="effort"></p-sortIcon>
                </th>
                <th pResizableColumn pSortableColumn="estimatedImpact.clicks" style="width: 120px" class="py-3 font-bold text-left">
                  Est. Clicks <p-sortIcon field="estimatedImpact.clicks"></p-sortIcon>
                </th>
                <th pResizableColumn style="width: 160px" class="py-3 font-bold text-left">Signal Strength</th>
                <th pResizableColumn pSortableColumn="businessImpactScore" style="width: 130px" class="py-3 font-bold text-left">
                  Business Impact <p-sortIcon field="businessImpactScore"></p-sortIcon>
                </th>
                <th pResizableColumn pSortableColumn="status" style="width: 160px" class="py-3 font-bold text-left">
                  Status <p-sortIcon field="status"></p-sortIcon>
                </th>
                <th pResizableColumn pSortableColumn="priority" style="width: 90px" class="py-3 font-bold text-left pr-4">
                  Priority <p-sortIcon field="priority"></p-sortIcon>
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item" class="cursor-pointer border-bottom-1 border-100 transition-colors hover:surface-50">
                <!-- Type Badge -->
                <td class="pl-4 py-3">
                  <span [ngClass]="getTypeBadgeClass(item.type)">
                    {{ getTypeLabel(item.type) }}
                  </span>
                </td>

                <!-- Title + Description -->
                <td class="py-3">
                  <div class="font-medium">{{ item.title }}</div>
                  <div class="text-sm text-600 mt-1" *ngIf="item.description">
                    {{ item.description }}
                  </div>
                </td>

                <!-- Effort -->
                <td class="py-3">
                  <span [ngClass]="getEffortBadgeClass(item.effort)">{{ item.effort }}</span>
                </td>

                <!-- Estimated Clicks -->
                <td class="py-3 text-600">{{ item.estimatedImpact?.clicks | number }}</td>

                <!-- Signal Strength -->
                <td class="py-3">
                  <p-progressBar
                    [value]="item.confidence * 100"
                    [showValue]="false"
                    styleClass="h-1rem mb-1">
                  </p-progressBar>
                  <small class="text-600">{{ item.confidence * 100 | number:'1.0-0' }}%</small>
                </td>

                <!-- Business Impact Score -->
                <td class="py-3">
                  <span
                    *ngIf="item.businessImpactScore"
                    [ngClass]="getImpactBadgeClass(item.businessImpactScore)">
                    {{ item.businessImpactScore }}
                  </span>
                  <span *ngIf="!item.businessImpactScore" class="text-600">—</span>
                </td>

                <!-- Status Dropdown or Read-only -->
                <td class="py-3" (click)="$event.stopPropagation()">
                  <p-dropdown
                    *ngIf="canEdit; else statusLabel"
                    [options]="statusOptions"
                    [(ngModel)]="item.status"
                    optionLabel="label"
                    optionValue="value"
                    [style]="{ width: '140px' }"
                    (onChange)="onInlineStatusChange(item, $event.value)">
                  </p-dropdown>
                  <ng-template #statusLabel>
                    <span>{{ item.status }}</span>
                  </ng-template>
                </td>

                <!-- Priority -->
                <td class="py-3 pr-4 text-600">{{ item.priority }}</td>
              </tr>
            </ng-template>

          </p-table>
        </div>
      </ng-container>

  </section>
</div>

<!-- Detail Side Panel -->
<gig-opportunity-detail-panel
  [(visible)]="isPanelVisible"
  [opportunity]="selectedItem"
  [canEdit]="canEdit"
  (save)="onPanelSave($event)"
  (visibleChange)="onPanelClose()">
</gig-opportunity-detail-panel>

<p-dialog
  header="Inspect URL"
  [(visible)]="showInspectDialog"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '640px', maxWidth: '95vw' }">
  <div class="p-fluid">
    <div class="mb-3" *ngIf="sitemapUrlOptions.length > 0">
      <label for="sitemap-url-select" class="font-medium block mb-2">Use saved sitemap URL</label>
      <p-dropdown
        id="sitemap-url-select"
        [options]="sitemapUrlOptions"
        [(ngModel)]="selectedSitemapUrl"
        optionLabel="label"
        optionValue="value"
        placeholder="Select a saved sitemap URL"
        [showClear]="true"
        [filter]="true"
        (onChange)="onSitemapUrlSelected()"
        styleClass="w-full">
      </p-dropdown>
    </div>

    <label for="inspect-url-input" class="font-medium block mb-2">URL to inspect</label>
    <input
      id="inspect-url-input"
      pInputText
      [(ngModel)]="inspectUrlInput"
      placeholder="https://example.com/page"
      class="w-full" />

    <small class="text-500 block mt-2 mb-3">
      Site URL source: {{ inspectSiteUrl || 'Not set in project settings' }}
    </small>

    <div *ngIf="inspectResult" class="border-round border-1 surface-border p-3">
      <div class="mb-2"><strong>Verdict:</strong> {{ inspectResult.indexStatusResult?.verdict || 'N/A' }}</div>
      <div class="mb-2"><strong>Coverage:</strong> {{ inspectResult.indexStatusResult?.coverageState || 'N/A' }}</div>
      <div class="mb-2"><strong>Indexing State:</strong> {{ inspectResult.indexStatusResult?.indexingState || 'N/A' }}</div>
      <div class="mb-2"><strong>Page Fetch:</strong> {{ inspectResult.indexStatusResult?.pageFetchState || 'N/A' }}</div>
      <div class="mb-2"><strong>Last Crawl:</strong> {{ inspectResult.indexStatusResult?.lastCrawlTime || 'N/A' }}</div>
      <div class="mb-2"><strong>Google Canonical:</strong> {{ inspectResult.indexStatusResult?.googleCanonical || 'N/A' }}</div>
      <div><strong>User Canonical:</strong> {{ inspectResult.indexStatusResult?.userCanonical || 'N/A' }}</div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button
      pButton
      label="Close"
      class="p-button-text"
      (click)="showInspectDialog = false">
    </button>
    <button
      pButton
      label="Inspect"
      icon="pi pi-search"
      [disabled]="!inspectUrlInput || isInspecting"
      [loading]="isInspecting"
      (click)="onInspectUrl()">
    </button>
  </ng-template>
</p-dialog>

<!-- Confirm Dialog for Run Analysis -->
<p-confirmDialog header="Confirmation" icon="pi pi-exclamation-triangle"></p-confirmDialog>
